import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFirestore, collection, doc, getDoc, setDoc, addDoc, onSnapshot, query, where, orderBy, limit, getDocs, deleteDoc, updateDoc, writeBatch, Firestore } from 'firebase/firestore';
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut, Auth } from 'firebase/auth';
import {Location} from '@angular/common';
import {Router} from '@angular/router';
import {Observable} from 'rxjs';

const chunkArray = (array, chunk) => {
    let tempArray = [];
    for (let i = 0; i < array.length; i += chunk) {
        tempArray = [...tempArray, array.slice(i, i + chunk)];
    }
    return tempArray;
};

@Injectable({
    providedIn: 'root'
})
export class FirebaseService {

    private app: FirebaseApp;
    private db: Firestore;
    private auth: Auth;
    private provider: GoogleAuthProvider;
    private token: string = null;
    private user: any = null;

    authUser: Observable<any>;

    constructor(private location: Location, private router: Router) {
        console.log('# FirebaseService');
        this.getLocalUser();
        this.init();

        this.authUser = new Observable<any>(obs => {
            onAuthStateChanged(this.auth, (user: any) => obs.next(user));
        });
    }

    public init() {
        this.app = initializeApp(environment.firebaseConfig);
        this.db = getFirestore(this.app);
        this.auth = getAuth(this.app);
        this.provider = new GoogleAuthProvider();
        this.provider.setCustomParameters({prompt: 'select_account'});
        
        if (this.app) {
            console.log(this.app.name);
            this.verifyLogged();
        }
    }

    private getLocalUser() {
        const local = sessionStorage.getItem('___u') || null;
        if (local !== null) {
            this.user = JSON.parse(local);
        }
    }

    private verifyLogged() {
        console.log('# verifyLogged');
        onAuthStateChanged(this.auth, (user: any) => {
            if (user) {
                this.user = user;
                if (this.location.path().indexOf('login') > -1) {
                    this.router.navigate(['/']);
                }
            } else {
                // User is signed out.
                this.user = null;
            }
        });
    }

    private createUser(user: any) {
        const displayName = user.displayName;
        const email = user.email;
        const photoURL = user.photoURL;
        const uid = user.uid;

        const userDocRef = doc(this.db, 'users', uid);
        getDoc(userDocRef).then((docSnap) => {
            console.log('# user', docSnap.exists());
            if (!docSnap.exists()) {
                setDoc(userDocRef, {
                    displayName,
                    email,
                    photoURL
                });
            }
        });
    }

    public getLoggedUser() {
        return this.user;
    }

    public login(): Promise<any> {
        return signInWithPopup(this.auth, this.provider).then((result: any) => {
            console.log(result);
            // This gives you a Google Access Token. You can use it to access the Google API.
            this.token = result.credential?.accessToken;
            // The signed-in user info.
            this.user = result.user;
            // ...
            this.createUser(result.user);
            sessionStorage.setItem('___u', JSON.stringify(this.user));
        }).catch((error: any) => {
            // Handle Errors here.
            const errorCode = error.code;
            const errorMessage = error.message;
            // The email of the user's account used.
            const email = error.email;
            // The firebase.auth.AuthCredential type that was used.
            const credential = error.credential;
            // ...
            sessionStorage.clear();
        });
    }

    public logout() {
        signOut(this.auth).then(() => {
            // Sign-out successful.
            sessionStorage.clear();
            this.router.navigate(['/login']);
        }).catch((error) => {
            // An error happened.
        });
    }

    public create(resource: string, value: any, id = null): Promise<unknown> {
        const timestamp = new Date().getTime();
        value = clearUndefined({
            ...value,
            uid: this.user.uid,
            createdAt: value.createdAt || timestamp,
            updatedAt: timestamp,
        });
        
        if (id !== null) {
            const docRef = doc(this.db, resource, id);
            return setDoc(docRef, value, {merge: true});
        } else {
            const collectionRef = collection(this.db, resource);
            return addDoc(collectionRef, value);
        }
    }

    public getOnce(resource: string, id: string): Promise<any> {
        const docRef = doc(this.db, resource, id);
        return getDoc(docRef).then(docSnap => ({...docSnap.data(), id: docSnap.id}));
    }

    public get(resource: string, id: string): Observable<any> {
        return new Observable<any>(obs => {
            const docRef = doc(this.db, resource, id);
            onSnapshot(docRef, (docSnap) => obs.next({...docSnap.data(), id: docSnap.id}));
        });
    }

    public listOnce_(resource: string, whereConditions: Array<any[]> = [], limitCount = 0) {
        const collectionRef = collection(this.db, resource);
        let q = query(collectionRef);
        
        if (whereConditions.length) {
            for (const wh of whereConditions) {
                q = query(q, where(wh[0], wh[1], wh[2]));
            }
        }
        if (limitCount > 0) {
            q = query(q, limit(limitCount));
        }
        return getDocs(q).then(valuesWithId);
    }

    public listOnce(resource: string, whereConditions: Array<any[]> = [], args: any = {limit: 0}): Promise<any> {
        const collectionRef = collection(this.db, resource);
        let q = query(collectionRef);
        
        if (whereConditions.length) {
            for (const wh of whereConditions) {
                q = query(q, where(wh[0], wh[1], wh[2]));
            }
        }
        if (args.hasOwnProperty('limit') && args.limit > 0) {
            q = query(q, limit(args.limit));
        }
        if (args.hasOwnProperty('sort')) {
            let sort = [];
            if (!(args.sort instanceof Array)) sort.push(args.sort);
            else sort = args.sort;

            for (const s of sort) {
                const split = s.split(' ');
                q = query(q, orderBy(split[0], split[1] ? split[1] : 'asc'));
            }
        }
        return getDocs(q).then(values => valuesWithId(values));
    }

    public list(resource: string, whereConditions: Array<any[]> = [], args: any = {limit: 0}): Observable<any> {
        const collectionRef = collection(this.db, resource);
        let q = query(collectionRef);
        
        if (whereConditions.length) {
            for (const wh of whereConditions) {
                q = query(q, where(wh[0], wh[1], wh[2]));
            }
        }
        if (args.hasOwnProperty('limit') && args.limit > 0) {
            q = query(q, limit(args.limit));
        }
        if (args.hasOwnProperty('sort')) {
            const sort = args.sort.split(' ');
            q = query(q, orderBy(sort[0], sort[1] ? sort[1] : 'asc'));
        }
        
        return new Observable<any[]>(obs => {
            onSnapshot(q, snapshot => obs.next(valuesWithId(snapshot)));
        });
    }

    public remove(resource: string, item: any): Promise<any> {
        const docRef = doc(this.db, resource, item.id);
        return deleteDoc(docRef);
    }

    public update(resource: string, item: any): Promise<any> {
        item.updatedAt = new Date().getTime();
        const docRef = doc(this.db, resource, item.id);
        return setDoc(docRef, clearUndefined(item), {merge: true});
    }

    public batch(resource: string, items: any[], action = 'create'): Promise<any> {
        return new Promise<any>(async (resolve, reject) => {
            if (items.length > 499) {
                const splited = chunkArray(items, 499);

                for (const [i, spl] of splited.entries()) {
                    const batch = writeBatch(this.db);
                    for (const docData of spl) {
                        if (docData) {
                            const id = docData.id || doc(collection(this.db, '_')).id;
                            const docRef = doc(this.db, resource, id);
                            if (action === 'create') {
                                batch.set(docRef, {...docData, uid: this.user.uid});
                            }
                            if (action === 'update') {
                                batch.update(docRef, {...docData, uid: this.user.uid});
                            }
                            if (action === 'delete') {
                                batch.delete(docRef);
                            }
                        }
                    }
                    try {
                        await batch.commit();
                        resolve(true);
                        console.log(resource, items.length, i);
                    } catch (e: any) {
                        console.log('# ERRO:', e.message);
                        reject(e.message);
                    }
                }
            } else {
                const batch = writeBatch(this.db);
                for (const spl of items) {
                    if (spl) {
                        const id = spl.id || doc(collection(this.db, '_')).id;
                        const docRef = doc(this.db, resource, id);
                        if (action === 'create') {
                            batch.set(docRef, {...spl, uid: this.user.uid});
                        }
                        if (action === 'update') {
                            batch.update(docRef, {...spl, uid: this.user.uid});
                        }
                        if (action === 'delete') {
                            batch.delete(docRef);
                        }
                    }
                }
                try {
                    await batch.commit();
                    resolve(true);
                    console.log(resource, items.length);
                } catch (e: any) {
                    console.log('# ERRO:', e.message);
                    reject(e.message);
                }
            }
        });
    }

    public createId(): string {
        return doc(collection(this.db, '_')).id;
    }
}

const clearUndefined = obj => {
    for (const prop in obj) {
        if (obj[prop] === undefined) {
            delete obj[prop];
        }
    }
    return obj;
};

const valuesWithId = snapshot => {
    const values = [];
    snapshot.forEach(item => values.push({id: item.id, ...item.data()}));
    return values;
};
